import React, { useEffect } from 'react';
import { CornerDownLeft, Keyboard, PackagePlus, X } from 'lucide-react';

/**
 * Keyboard Shortcuts Modal
 * @param {boolean} open
 * @param {function} onClose
 */
export default function KeyboardShortcutsModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Keyboard size={22} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Keyboard Shortcuts</h3>
              <p className="text-xs text-teal-100">Quick actions for faster inventory handling</p>
            </div>
          </div>
          <button
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
          <Section title="Inventory">
            <ShortcutRow
              icon={<PackagePlus size={16} className="text-indigo-600" />}
              label="Add Item"
              keyLabel="F2"
              rowClassName="bg-indigo-50 border-indigo-100"
              keyClassName="bg-indigo-600"
            />
            <ShortcutRow
              icon={<CornerDownLeft size={16} className="text-emerald-600" />}
              label="Save Added Item"
              keyLabel="Enter"
              rowClassName="bg-emerald-50 border-emerald-100"
              keyClassName="bg-emerald-600"
            />
            <ShortcutRow
              icon={<X size={16} className="text-red-500" />}
              label="Cancel/Close Add Item"
              keyLabel="Esc"
              rowClassName="bg-red-50 border-red-100"
              keyClassName="bg-red-600"
            />
          </Section>
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center">
            Press <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[11px] font-bold text-slate-700">Esc</kbd> or click outside to close
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ShortcutRow({ icon, label, keyLabel, rowClassName, keyClassName }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${rowClassName}`}>
      <div className="flex items-center gap-3">
        {icon}
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <kbd className={`px-2 py-1 text-white text-xs font-bold rounded ${keyClassName}`}>{keyLabel}</kbd>
    </div>
  );
}
